import {
  FiChevronLeft,
  FiChevronRight,
  FiClock,
} from "react-icons/fi";
import {
  DaySlotDto,
  WEEKDAYS,
  formatLongDay,
  formatMonthYear,
  formatSlotLabel,
  formatSlotRange,
  getMonthCells,
  isSameDay,
  startOfDay,
} from "../../utils/surgeryBookingCalendar";

type Props = {
  viewMonth: Date;
  selectedDate: Date;
  today: Date;
  slots: DaySlotDto[];
  selectedHours: number[];
  loading?: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDate: (date: Date) => void;
  onToggleHour: (hour: number) => void;
  /** Admin: show booked count on full slots */
  showBookedMeta?: boolean;
  /** Admin: show "8:00 - 9:00" instead of "8:00 AM" */
  showRangeLabels?: boolean;
  readOnly?: boolean;
};

export default function SurgerySlotPicker({
  viewMonth,
  selectedDate,
  today,
  slots,
  selectedHours,
  loading,
  onPrevMonth,
  onNextMonth,
  onSelectDate,
  onToggleHour,
  showBookedMeta = false,
  showRangeLabels = false,
  readOnly = false,
}: Props) {
  const monthCells = getMonthCells(viewMonth);
  const todayStart = startOfDay(today);
  const openCount = slots.filter((s) => s.available || s.myBooking).length;
  const myBookedCount = slots.filter((s) => s.myBooking).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-gray-100">
      <div className="w-full px-4 md:px-6 py-4 md:py-5">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onPrevMonth}
            aria-label="Previous month"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>
          <p className="text-sm md:text-base font-semibold text-gray-900">
            {formatMonthYear(viewMonth)}
          </p>
          <button
            type="button"
            onClick={onNextMonth}
            aria-label="Next month"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 md:gap-2.5 mb-2">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="h-9 flex items-center justify-center text-xs md:text-sm font-medium text-gray-400"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2 md:gap-2.5">
          {monthCells.map((cell, idx) => {
            const { date, inMonth } = cell;
            const dayStart = startOfDay(date);
            const isPast = dayStart.getTime() < todayStart.getTime();
            const isSelected = isSameDay(date, selectedDate);
            const isToday = isSameDay(date, today);

            return (
              <div
                key={`${date.toISOString()}-${idx}`}
                className="flex items-center justify-center"
              >
                <button
                  type="button"
                  disabled={isPast}
                  onClick={() => onSelectDate(date)}
                  className={[
                    "flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-xl text-sm md:text-base transition-colors",
                    !inMonth && !isSelected ? "text-gray-300" : "",
                    isPast ? "text-gray-300 cursor-not-allowed" : "",
                    !isPast && !isSelected && inMonth
                      ? "text-gray-800 hover:bg-gray-100"
                      : "",
                    !isPast && !isSelected && !inMonth ? "hover:bg-gray-50" : "",
                    isSelected
                      ? "bg-gray-900 text-white font-semibold shadow-sm"
                      : "",
                    isToday && !isSelected
                      ? "font-semibold ring-1 ring-inset ring-gray-300"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {date.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="w-full flex flex-col border-t border-gray-100 lg:border-t-0 px-4 md:px-6 py-4 md:py-5">
        <div className="flex items-center justify-between gap-2 mb-4">
          <p className="text-sm md:text-base font-semibold text-gray-900">
            {formatLongDay(selectedDate)}
          </p>
          <p className="text-xs text-gray-500">
            {loading
              ? "Loading…"
              : myBookedCount > 0
                ? `${myBookedCount} yours · ${openCount} open`
                : `${openCount} slot${openCount === 1 ? "" : "s"} open`}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 flex-1 content-start">
          {slots.map((slot) => {
            const isMine = slot.myBooking;
            const isActive = selectedHours.includes(slot.hour);
            const isFull = !slot.available && !isMine && !slot.isPast;
            const disabled =
              readOnly || slot.isPast || (isFull && !isMine) || isMine;

            return (
              <button
                key={slot.hour}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (readOnly || isMine || slot.isPast || isFull) return;
                  onToggleHour(slot.hour);
                }}
                aria-pressed={isActive || isMine}
                title={
                  isMine
                    ? slot.ist_label
                      ? `Booked · ${slot.ist_label}`
                      : "Already booked by you"
                    : isFull
                      ? showBookedMeta
                        ? `Full (${slot.bookedCount}/${slot.capacity})${slot.ist_label ? ` · ${slot.ist_label}` : ""}`
                        : "Unavailable"
                      : slot.isPast
                        ? "Past"
                        : slot.ist_label || undefined
                }
                className={[
                  "flex flex-col items-center justify-center gap-0.5 rounded-full px-3 py-2 md:py-2.5 text-xs md:text-sm transition-all min-h-[2.75rem] disabled:opacity-100",
                  isMine
                    ? "bg-emerald-500 text-white font-medium shadow-sm cursor-default"
                    : slot.isPast || isFull || readOnly
                      ? "bg-transparent text-gray-300 cursor-not-allowed"
                      : isActive
                        ? "bg-gray-900 text-white font-medium shadow-sm"
                        : "border border-gray-200 text-gray-800 hover:border-gray-400 hover:bg-gray-50",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span className="flex items-center gap-1.5">
                  <FiClock
                    className={[
                      "h-3.5 w-3.5 shrink-0",
                      isMine
                        ? "text-white"
                        : slot.isPast || isFull
                          ? "text-gray-300"
                          : isActive
                            ? "text-white"
                            : "text-gray-500",
                    ].join(" ")}
                    aria-hidden
                  />
                  {showRangeLabels
                    ? formatSlotRange(slot.hour)
                    : formatSlotLabel(slot.hour)}
                </span>
                {slot.ist_range_24 || slot.ist_label ? (
                  <span
                    className={`text-[9px] leading-tight text-center px-1 ${
                      isMine
                        ? "text-emerald-100"
                        : slot.isPast || isFull
                          ? "text-gray-300"
                          : isActive
                            ? "text-gray-300"
                            : "text-gray-400"
                    }`}
                  >
                    IST {slot.ist_range_24 || slot.ist_label}
                  </span>
                ) : null}
                {showBookedMeta && slot.bookedCount > 0 && !isMine ? (
                  <span
                    className={`text-[10px] leading-none ${
                      isFull ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    {slot.bookedCount}/{slot.capacity}
                  </span>
                ) : null}
                {isMine ? (
                  <span className="text-[10px] leading-none text-emerald-100">
                    Booked
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
